import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, Button, Card, Container, Grid, Stack, SwipeableDrawer, TextField, Typography } from '@mui/material';
import { dispatch, useSelector } from '../../../../redux/store';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import useSettings from '../../../../hooks/useSettings';
import Page from '../../../../components/Page';
import Iconify from '../../../../components/Iconify';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../../components/table/DataGridTable';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { accessVerify, capitalize } from '../../../../utils/common';
import { fCurrency } from '../../../../utils/formatNumber';
import PermissionRequired from '../../../errorPages/permissionRequired';
import { addPickmeAccount, getAllPickmeAccount, updatePickmeAccount } from '../../../../redux/slices/saleRedux/pickmeAccountRedux';
import { getLocations } from '../../../../redux/slices/settingsManagementRedux/locationsRedux';

export default function PickmeAccountManager() {
  const dataModel = {
    name: '',
    locationId: '',
    key: '',
  };
  const [selectedDataObj, setSelectedDataObj] = useState(dataModel);
  const { themeStretch } = useSettings();
  const [isAdd, setIsAdd] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [pickmeAccounts, setPickmeAccounts] = useState([]);

  const { allData, addData } = useSelector((state) => state.pickmeAccount);
  let locationData;
  ({ data: locationData } = useSelector((state) => state.location));

  useEffect(() => {
    dispatch(getAllPickmeAccount());
    dispatch(getLocations());
  }, []);

  useEffect(() => {
    setPickmeAccounts(
      allData?.map((value) => {
        return {
          ...value,
        };
      })
    );
    setIsModelOpen(false);
  }, [allData]);

  const manageModel = (modelData, type) => {
    if (isModelOpen) {
      setIsModelOpen(false);
    } else {
      setIsModelOpen(true);
      setIsAdd(type === 'add');
      if (type === 'add') {
        setSelectedDataObj(dataModel);
      } else {
        setSelectedDataObj(modelData);
      }
    }
  };

  useEffect(() => {
    if (addData?.data) {
      dispatch(getAllPickmeAccount());
    }
  }, [addData]);

  const updateEditingData = (key, value) => {
    setSelectedDataObj({
      ...selectedDataObj,
      [key]: value,
    });
  };

  const columns = !pickmeAccounts?.[0]
    ? []
    : Object.keys(pickmeAccounts?.[0])
        .map((value) => {
          if ([].includes(value)) {
            return {};
          } else if (['locationId'].includes(value)) {
            return {
              accessorKey: 'locationId',
              header: 'Location Name',
              Cell: ({ cell, row }) => {
                return locationData?.find((value) => value?.id === row?.original?.locationId)?.name;
              },
            };
          } else if (value === 'depreciatedAmountPerDay') {
            return {
              accessorKey: 'totalValue',
              header: 'Depreciated Amount Per Day',
              Cell: ({ cell, row }) => {
                return fCurrency(row?.original?.depreciatedAmountPerDay);
              },
            };
          } else {
            return {
              accessorKey: value,
              header: capitalize(value),
            };
          }
        })
        ?.filter((value) => value?.accessorKey);

  const [rowSelection, setRowSelection] = useState(false);

  const handleSubmit = () => {
    if (selectedDataObj.name.length === 0) {
      toast.error('Pickme Account name is required!');
    } else if (selectedDataObj.locationId.length === 0) {
      toast.error('Location is required!');
    } else if (selectedDataObj.key.length === 0) {
      toast.error('Key is required!');
    } else {
      dispatch(isAdd ? addPickmeAccount(selectedDataObj) : updatePickmeAccount(selectedDataObj));
    }
  };

  return (
    <Page title={`Pickme Account Manager`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={`Pickme Account Manager`}
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            {
              name: 'Pickme',
              href: '',
            },
            { name: `Pickme Account Manager` },
          ]}
          action={
            <Button
              variant='contained'
              startIcon={<Iconify icon='eva:plus-fill' />}
              onClick={() => {
                manageModel(dataModel, 'add');
              }}
            >
              Add Account
            </Button>
          }
        />

        {accessVerify('SUPER_ADMIN') ? (
          <DataGridTable
            name={'PickMe Account Manager'}
            data={pickmeAccounts}
            column={columns}
            onRowClick={(row) => {
              manageModel(row?.original, 'edit');
            }}
            isRowClickable={true}
            isLoading={false}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            enableRowSelection={false}
            enableRowActions={false}
            renderRowActionItems={(value, closeMenu) => []}
          />
        ) : (
          <PermissionRequired />
        )}

        <SwipeableDrawer
          anchor={'top'}
          open={isModelOpen}
          onOpen={() => setIsModelOpen(true)}
          onClose={() => setIsModelOpen(false)}
          PaperProps={{
            sx: {
              width: '90%',
              height: '90%',
              marginLeft: '5%',
              marginTop: '3%',
              borderRadius: '10px',
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <h1>{isAdd ? 'Add' : 'Edit'} Pickme Account</h1>
          </Box>
          <Grid container spacing={1}>
            <Grid item xs={12} md={8}>
              <Stack spacing={1} sx={{ p: 1 }}>
                <Card sx={{ p: 3 }}>
                  <Typography variant='subtitle1' padding='10px 0'>
                    Pickme Account Details
                  </Typography>
                  <Stack spacing={3}>
                    <TextField fullWidth label='Name' value={selectedDataObj?.name} onChange={(e) => updateEditingData('name', e.target.value)} />

                    <Autocomplete
                      onChange={(event, newValue) => {
                        const userInfo = locationData.find((value) => value?.id === newValue?.value);
                        updateEditingData('locationId', userInfo?.id);
                      }}
                      options={locationData?.map((value) => ({
                        label: `${value.name}`,
                        value: value.id,
                      }))}
                      value={{
                        label: `${locationData?.find((value) => value.id === selectedDataObj?.locationId)?.name || 'Location Not Found'}`,
                        value: selectedDataObj?.locationId,
                      }}
                      renderInput={(params) => <TextField label='Location' {...params} />}
                    />
                    <TextField fullWidth label='Key' value={selectedDataObj?.key} onChange={(e) => updateEditingData('key', e.target.value)} />
                  </Stack>
                </Card>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack spacing={1} sx={{ p: 1 }}>
                <Card sx={{ p: 3 }}>
                  <Stack spacing={3} sx={{ p: 3 }}>
                    <LoadingButton type='submit' variant='contained' size='large' style={{ width: '100%' }} onClick={handleSubmit}>
                      {isAdd ? 'Add Pickme Account' : 'Save Changes'}
                    </LoadingButton>
                    <Button
                      color='info'
                      variant='outlined'
                      size='large'
                      style={{ width: '100%' }}
                      onClick={() => {
                        manageModel(dataModel, 'add');
                      }}
                    >
                      Close
                    </Button>
                  </Stack>
                </Card>
              </Stack>
            </Grid>
          </Grid>
        </SwipeableDrawer>
      </Container>
    </Page>
  );
}
